import Dashboard, {
    DashboardAccess,
    DashboardAccessVisibility,
    DashboardFrame,
    DashboardPermittedAction,
    DashboardPermittedActions,
    UserGroupLevel,
} from 'domain/entity/dashboard';
import { DASHBOARD_TYPE } from 'domain/entity/reportTemplate';
import { resolveInternalTranslationKey } from 'asset/translation/resolveInternalTranslationKey';

type DashboardUserGroupJson = {
    id: string;
    name: string;
    level: UserGroupLevel;
};

type DashboardAccessJson = {
    visibility: string;
    groups: DashboardUserGroupJson[];
};

export type DashboardFrameJson = {
    header: {
        logo: boolean;
        ['template_name']: boolean;
        period: boolean;
        location: boolean;
    };
    footer: {
        ['generation_date']: boolean;
        ['page_number']: boolean;
    };
};

export type DashboardJson = {
    id: string;
    name: string;
    pinned: boolean;
    access: DashboardAccessJson;
    owner: { me: boolean; name?: string; email?: string };
    ['permitted_actions']: string[];
    starred: boolean;
    type: DASHBOARD_TYPE;
    description: string;
    frame: DashboardFrameJson;
};

export const asDashboardPermittedActions = (dashboardPermittedActionsJson: string[]): DashboardPermittedActions => ({
    delete: dashboardPermittedActionsJson.includes(DashboardPermittedAction.DELETE),
    edit: dashboardPermittedActionsJson.includes(DashboardPermittedAction.EDIT),
    link: dashboardPermittedActionsJson.includes(DashboardPermittedAction.LINK),
    share: dashboardPermittedActionsJson.includes(DashboardPermittedAction.SHARE),
    pin: dashboardPermittedActionsJson.includes(DashboardPermittedAction.PIN),
    duplicate: dashboardPermittedActionsJson.includes(DashboardPermittedAction.DUPLICATE),
});

export const asDashboardAccess = (accessJson: DashboardAccessJson): DashboardAccess => ({
    visibility: accessJson.visibility as DashboardAccessVisibility,
    groups: accessJson.groups,
});

export const asDashboardFrame = (frameJson: DashboardFrameJson): DashboardFrame => ({
    header: {
        logo: frameJson?.header.logo ?? false,
        templateName: frameJson?.header.template_name ?? false,
        period: frameJson?.header.period ?? false,
        location: frameJson?.header.location ?? false,
    },
    footer: {
        generationDate: frameJson?.footer.generation_date ?? false,
        pageNumber: frameJson?.footer.page_number ?? false,
    },
});

export const asDashboard = (dashboardJson: DashboardJson): Dashboard => {
    if (dashboardJson.id === undefined) {
        throw new ReferenceError('Undefined required Id');
    }
    return new Dashboard(
        dashboardJson.id,
        resolveInternalTranslationKey(dashboardJson.name),
        dashboardJson.pinned,
        asDashboardAccess(dashboardJson.access),
        dashboardJson.owner,
        asDashboardPermittedActions(dashboardJson.permitted_actions),
        dashboardJson.starred,
        dashboardJson.type,
        resolveInternalTranslationKey(dashboardJson.description),
        asDashboardFrame(dashboardJson.frame)
    );
};

export const asDashboardList = (json: DashboardJson[]): Dashboard[] => json.map(asDashboard);
